import { useState, useCallback, useContext } from 'react';
import axios from 'axios';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { ChatContext } from '../context/ChatContext';

const CustomerSearchModal = ({ isOpen, onClose }) => {
  const { data: chatData } = useContext(ChatContext);
  const [accountNumber, setAccountNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [newCommentText, setNewCommentText] = useState('');
  const [commentStatus, setCommentStatus] = useState({ message: '', type: '' });

  const API_URL = 'https://portal.silulumanzi.com/login/bridge/';

  const fetchCustomerData = useCallback(async (accNum) => {
    if (!accNum.trim()) {
      setError("Account number cannot be empty.");
      setCustomerData(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    setCustomerData(null);

    const baseAccountDataStructure = {
      account: {},
      balance: 0,
      balance_data: {},
      comments: [],
      connection_status: 0,
      location: [],
      transactions: [],
      consumption: null, // Add new field for consumption data
    };
    let fetched_customer_data = {};

    try {
      const formData = new FormData();
      formData.append('authenticated', true);
      formData.append('account_number', accNum);

      // 1. Get account info
      const response = await axios.post(API_URL, formData);
      if (response.data.status == 1) {
        fetched_customer_data.account = response.data.account;

        // 3. Get balance & transactions (simplified for modal)
        const formDataBalance = new FormData();
        formDataBalance.append('getBalance', true);
        formDataBalance.append('account_number', accNum);
        formDataBalance.append('billing_date', new Date().toISOString().split('T')[0]); // today

        const balanceRes = await axios.post(API_URL, formDataBalance);
        if (balanceRes.data.status == 1) {
          fetched_customer_data.balance_data = balanceRes.data;
          fetched_customer_data.balance = parseFloat(balanceRes.data.last_statement.CL_BLNCE).toFixed(2);
        }

        // 4. Get comments (simplified - maybe just latest or count)
        const formDataComments = new FormData();
        formDataComments.append('comments', true);
        formDataComments.append('account_number', accNum);
        formDataComments.append('comment_user', chatData.user?.name || "System");

        const commentsRes = await axios.post(API_URL, formDataComments);
        if (commentsRes.data.status == 1 && commentsRes.data.comments) {
          fetched_customer_data.comments = commentsRes.data.comments.reverse().slice(0, 3); // Show latest 3
        }

        // 5. Get account status
        const formDataStatus = new FormData();
        formDataStatus.append('getAccountStatus', true);
        formDataStatus.append('account_number', accNum);

        const statusRes = await axios.post(API_URL, formDataStatus);
        // Ensure CON_STATUS exists and parse it to an integer
        if (statusRes.data.status == 1 && statusRes.data.account_status && statusRes.data.account_status.CON_STATUS !== undefined) {
          fetched_customer_data.connection_status = parseInt(statusRes.data.account_status.CON_STATUS, 10);
        } else {
          fetched_customer_data.connection_status = 0; // Default to a non-connected state (e.g., 0 or 2 for disconnected)
        }

        // 6. Get Consumption/Graph Data
        const formDataGraph = new FormData();
        formDataGraph.append('getGraphData', true);
        formDataGraph.append('account_number', accNum);
        formDataGraph.append('billing_date', new Date().toISOString().split('T')[0]); // today

        const graphRes = await axios.post(API_URL, formDataGraph);
        if (graphRes.data) {
          let consumption;
          if (typeof graphRes.data === 'string') {
            try {
              consumption = JSON.parse(graphRes.data);
            } catch (e) {
              console.error("Error parsing consumption data (string):", e);
              console.error("Raw consumption response (string):", graphRes.data);
              // Set an error or handle as appropriate, e.g., fetched_customer_data.consumption = { error: "Invalid format" };
            }
          } else if (typeof graphRes.data === 'object') {
            consumption = graphRes.data; // Assume it's already a parsed object
          }

          if (Array.isArray(consumption) && consumption.length > 0) {
            const consumptionTableData = consumption.map(item => ({
              date_to: item.date_to ? item.date_to.substring(0, 10) : 'N/A',
              consump: item.consump ? `${String(item.consump).split(".")[0]}kl` : 'N/A',
              bilng_date: item.bilng_date ? item.bilng_date.substring(0, 10) : 'N/A',
            }));
            fetched_customer_data.consumption = { table_data: consumptionTableData };
          } else if (consumption) { // It was parsed/an object but not a non-empty array
            console.warn("Consumption data received but is not a non-empty array:", consumption);
            }
        }
        setCustomerData({ ...baseAccountDataStructure, ...fetched_customer_data });
      } else {
        setError(response.data.message || "Customer not found or error fetching data.");
      }
    } catch (err) {
      console.error("Error fetching customer data:", err);
      setError("An error occurred while fetching customer data.");
    } finally {
      setIsLoading(false);
    }
  }, [chatData.user?.name]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCustomerData(accountNumber);
  };

  const handleCommentSubmitInModal = async (e) => {
    e.preventDefault();
    if (!newCommentText.trim()) {
      setCommentStatus({ message: "Comment cannot be empty.", type: "error" });
      return;
    }
    if (!customerData || !customerData.account || !customerData.account.ACCOUNT_NO) {
      setCommentStatus({ message: "Customer data not loaded. Cannot submit comment.", type: "error" });
      return;
    }

    const formData = new FormData();
    formData.append('comments', true);
    formData.append('account_number', customerData.account.ACCOUNT_NO);
    // Use edams_user if available from fetched data, otherwise fallback to chatData.user.name
    const commentUser = customerData.account.edams_user || chatData.user?.name || "System";
    formData.append('comment_user', commentUser);
    formData.append('comment_text', newCommentText);

    setCommentStatus({ message: "Submitting...", type: "loading" });
    try {
      const response = await axios.post(API_URL, formData);
      if (response.data && response.data.status == 1) {
        setCommentStatus({ message: response.data.message || "Comment submitted successfully!", type: "success" });
        setNewCommentText("");
        // Optionally, re-fetch customer data to show the new comment
        fetchCustomerData(customerData.account.ACCOUNT_NO);
      } else {
        setCommentStatus({ message: response.data.message || "Failed to submit comment.", type: "error" });
      }
    } catch (err) {
      console.error("Error submitting comment from modal:", err);
      setCommentStatus({ message: "An error occurred while submitting the comment.", type: "error" });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-start pt-10">
      <div className="relative mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white"> {/* Reverted to max-w-2xl, adjusted padding */}
        <div className="flex justify-between items-center mb-4 pb-3 border-b"> {/* Adjusted padding and border */}
          <h3 className="text-xl font-semibold text-gray-900">Customer Lookup</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSearch} className="flex items-center gap-3 mb-6"> {/* Increased gap */}
          <input // Search input
            type="text"
            value={accountNumber}
            onChange={(e) => setAccountNumber(e.target.value)}
            placeholder="Enter Account Number"
            className="flex-grow p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
          />
          <button
            type="submit"
            disabled={isLoading}
            className="bg-sky-500 hover:bg-sky-600 text-white font-semibold py-2 px-4 rounded-md disabled:opacity-50 flex items-center"
          >
            {isLoading ? (
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <MagnifyingGlassIcon className="h-5 w-5 mr-2" />
            )}
            Search
          </button>
        </form>

        {error && <p className="text-red-500 text-center mb-4">{error}</p>}

        {customerData && customerData.account && Object.keys(customerData.account).length > 0 && (
          <div className="space-y-5 text-sm"> {/* Adjusted spacing */}
            <h4 className="text-xl font-semibold text-sky-700"> {/* Adjusted name size */}
              {customerData.account.TITLE} {customerData.account.INITIAL} {customerData.account.SURNAME}
            </h4>
            
            {/* Top section: Header Info and Consumption Data side-by-side */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-x-6 gap-y-4">
              {/* Left Column: Header Info - reduced span to give more space to consumption */}
              <div className="md:col-span-2 space-y-3 p-3 border rounded-md bg-gray-50 shadow-sm">
                <div>
                  <p className="font-medium text-gray-600 text-xs">Account Number:</p>
                  <p className="text-gray-800">{customerData.account?.ACCOUNT_NO || accountNumber}</p>
                </div>
                <div>
                  <p className="font-medium text-gray-600 text-xs">Balance:</p>
                  <p className="text-gray-800">R{customerData.balance}</p>
                </div>
                <div>
                  <p className="font-medium text-gray-600 text-xs">Connection Status:</p>
                  <p className={`font-semibold ${customerData.connection_status === 1 ? 'text-green-600' : 'text-red-600'}`}>
                    {customerData.connection_status === 1 ? 'Connected' : 'Disconnected'}
                  </p>
                </div>
                <div>
                  <p className="font-medium text-gray-600 text-xs">Address:</p>
                  <p className="text-gray-800 leading-snug">
                    {customerData.account.UA_ADRESS1 && <>{customerData.account.UA_ADRESS1}<br /></>}
                    {customerData.account.UA_ADRESS2 && <>{customerData.account.UA_ADRESS2}<br /></>}
                    {customerData.account.UA_ADRESS3 && <>{customerData.account.UA_ADRESS3}<br /></>}
                    {customerData.account.UA_ADRESS4 && <>{customerData.account.UA_ADRESS4}<br /></>}
                    {customerData.account.UA_ADRESS5 && <>{customerData.account.UA_ADRESS5}</>}
                  </p>
                </div>
              </div>

              {/* Right Column: Consumption Data - increased span */}
              {customerData.consumption && customerData.consumption.table_data && (
                <div className="md:col-span-3 p-3 border rounded-md bg-white shadow-sm">
                  <h5 className="text-sm font-semibold text-gray-700 mb-2">Consumption Data</h5>
                  <div className="overflow-x-auto max-h-48"> {/* Max height for consumption table */}
                    <table className="min-w-full text-xs table-auto">
                      <thead className="bg-gray-100 sticky top-0 z-10">
                        <tr>
                          <th className="px-2 py-1 text-left font-medium text-gray-500">End Date</th>
                          <th className="px-2 py-1 text-left font-medium text-gray-500">Consumption</th>
                          <th className="px-2 py-1 text-left font-medium text-gray-500">Billing Date</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {customerData.consumption.table_data.map((item, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-2 py-1 whitespace-nowrap">{item.date_to}</td>
                            <td className="px-2 py-1 whitespace-nowrap">{item.consump}</td>
                            <td className="px-2 py-1 whitespace-nowrap">{item.bilng_date}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Bottom section: Comments and Add Comment Form */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 mt-4">
              {customerData.comments && customerData.comments.length > 0 && (
                <div className="p-3 border rounded-md bg-white shadow-sm">
                  <h5 className="text-sm font-semibold text-gray-700 mb-2">Recent Comments</h5>
                  <ul className="space-y-1.5 text-gray-600 max-h-32 overflow-y-auto text-xs pr-1">
                    {customerData.comments.map(comment => (
                      <li key={comment.EVENT_ID} className="pb-1 border-b border-gray-100 last:border-b-0">
                        <span className="font-semibold">{comment["Created By"]} ({comment["Created on"].substring(0,10)}):</span> {comment.Comment.substring(0, 80)}{comment.Comment.length > 80 ? '...' : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className={`p-3 border rounded-md bg-white shadow-sm ${!(customerData.comments && customerData.comments.length > 0) ? 'md:col-span-2' : ''}`}> {/* Span full if no comments */}
                <h5 className="text-sm font-semibold text-gray-700 mb-2">Add New Comment</h5>
                <form onSubmit={handleCommentSubmitInModal} className="space-y-2">
                <div>
                  <textarea
                    rows={3}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                    placeholder="Type your comment here..."
                    value={newCommentText}
                    onChange={(e) => setNewCommentText(e.target.value)}
                  />
                </div>
                {commentStatus.message && (
                  <p className={`text-xs ${commentStatus.type === 'error' ? 'text-red-500' : commentStatus.type === 'success' ? 'text-green-500' : 'text-gray-500'}`}>
                    {commentStatus.message}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={commentStatus.type === 'loading'}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-1.5 px-3 rounded-md disabled:opacity-50 text-xs w-full sm:w-auto">
                  {commentStatus.type === 'loading' ? 'Submitting...' : 'Submit Comment'}</button>
                </form>
                </div> {/* This div closes the "Add New Comment" block */}
              {/* The extraneous ")}" was here and has been removed. */}
            </div>
          </div>
        )}
        {customerData && !(customerData.account && Object.keys(customerData.account).length > 0) && !isLoading && !error && (
            <p className="text-gray-500 text-center">No customer data found for this account number.</p>
        )}
      </div>
    </div>
  );
};

export default CustomerSearchModal;